import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Tabs, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import {
  useAcceptInviteMutation,
  useSessionQuery,
  useSignInMutation,
  useSignOutMutation,
  useSignUpMutation,
} from "../lib/authQueries";
import { useTRPC } from "../lib/trpc";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg font-bold tracking-tight">
            Loot <span className="text-primary">OS</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">{children}</CardContent>
      </Card>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {children}
    </div>
  );
}

/**
 * The landing page for a tenant-invite link (/accept-invite?id=…), outside the
 * app shell — the visitor usually has no account yet. Flow: preview the invite
 * (public by unguessable id) → sign up or sign in as the invited email →
 * acceptInvitation → into the tenant. The signup hook admits invited emails even
 * when the platform-admin allowlist is on.
 */
export function AcceptInvitePage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { id } = useSearch({ strict: false }) as { id?: string };
  const invite = useQuery(trpc.invites.get.queryOptions(id ? { id } : { id: "" }));
  const { data: session, isPending: sessionPending } = useSessionQuery();
  const acceptInvite = useAcceptInviteMutation();
  const signIn = useSignInMutation();
  const signUp = useSignUpMutation();
  const signOut = useSignOutMutation();

  const [tab, setTab] = useState<"signup" | "signin">("signup");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!id) {
    return (
      <Shell>
        <ErrorBox>This invite link is missing its id. Ask for a fresh link.</ErrorBox>
      </Shell>
    );
  }
  if (invite.isLoading || sessionPending) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      </Shell>
    );
  }
  if (!invite.data) {
    return (
      <Shell>
        <ErrorBox>This invitation doesn't exist. Ask for a fresh link.</ErrorBox>
        <Link to="/login" className={buttonVariants({ variant: "outline" })}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  const { tenantName, tenantId, email, role, status, expired } = invite.data;

  if (status === "accepted") {
    return (
      <Shell>
        <p className="text-sm">
          This invitation to <strong>{tenantName}</strong> has already been accepted.
        </p>
        <Link to="/tenants/$tenantId" params={{ tenantId }} className={buttonVariants()}>
          Open {tenantName}
        </Link>
      </Shell>
    );
  }
  if (status !== "pending" || expired) {
    return (
      <Shell>
        <ErrorBox>
          This invitation to {tenantName} has {expired ? "expired" : "been canceled"}. Ask an admin
          to send a new one.
        </ErrorBox>
        <Link to="/login" className={buttonVariants({ variant: "outline" })}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  // Failures here and in submit are toasted by the query client; the page
  // only has to re-enable its button.
  const accept = async () => {
    setBusy(true);
    try {
      // Resolves only once me.tenants is refreshed, so TenantLayout can't render
      // from a membership list that predates the accept.
      await acceptInvite.mutateAsync(id);
      void navigate({ to: "/tenants/$tenantId", params: { tenantId }, replace: true });
    } catch {
      setBusy(false);
    }
  };

  // Signed in as someone else: accepting would attach the wrong account.
  if (session && session.user.email.toLowerCase() !== email.toLowerCase()) {
    return (
      <Shell>
        <p className="text-sm">
          This invitation to <strong>{tenantName}</strong> is for{" "}
          <span className="font-mono">{email}</span>, but you're signed in as{" "}
          <span className="font-mono">{session.user.email}</span>.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            // The mutation seeds the signed-out session, flipping this page
            // to the sign-up/sign-in form without a reload.
            signOut.mutate();
          }}
        >
          Sign out and start over
        </Button>
      </Shell>
    );
  }

  if (session) {
    return (
      <Shell>
        <p className="text-sm">
          You've been invited to <strong>{tenantName}</strong> as <strong>{role}</strong>.
        </p>
        <Button onClick={() => void accept()} disabled={busy}>
          {busy ? "Joining…" : `Join ${tenantName}`}
        </Button>
      </Shell>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "signup") {
        await signUp.mutateAsync({
          email,
          name: name.trim() || email.split("@")[0] || email,
          password,
        });
      } else {
        await signIn.mutateAsync({ email, password });
      }
      await accept();
    } catch {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <CardDescription>
        You've been invited to <strong>{tenantName}</strong> as <strong>{role}</strong>.
      </CardDescription>
      <Tabs value={tab} onValueChange={(v) => setTab(v as "signup" | "signin")}>
        <TabsList className="w-full">
          <TabsTrigger value="signup" className="flex-1">
            Create account
          </TabsTrigger>
          <TabsTrigger value="signin" className="flex-1">
            Sign in
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <form onSubmit={submit} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="invite-email">Email</Label>
          {/* The invitation names the email; accepting under another one would fail. */}
          <Input id="invite-email" type="email" value={email} readOnly className="bg-muted/50" />
        </div>
        {tab === "signup" && (
          <div className="grid gap-2">
            <Label htmlFor="invite-name">Name</Label>
            <Input
              id="invite-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
            />
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="invite-password">Password</Label>
          <Input
            id="invite-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? (tab === "signup" ? "Creating account…" : "Signing in…") : `Join ${tenantName}`}
        </Button>
      </form>
    </Shell>
  );
}
